import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { StatusBadge, CHECK_TYPE_LABEL } from "@/components/StatusBadge";
import CaseAttachments from "@/components/CaseAttachments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Pencil, AlertCircle } from "lucide-react";

const STATUS_OPTIONS = ["Pending", "In Progress", "Completed", "On Hold"];

function isOverdue(c) {
  if (!c.promised_by || c.status === "Completed") return false;
  const p = new Date(c.promised_by);
  return !isNaN(p.getTime()) && p.getTime() < Date.now();
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [checks, setChecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ status: "", remarks: "", promised_by: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([api.get("/checks/stats"), api.get("/checks")]);
      setStats(s.data); setChecks(c.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      status: c.status,
      remarks: c.remarks || "",
      promised_by: c.promised_by ? c.promised_by.slice(0, 10) : "",
    });
  };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const patched = await api.patch(`/checks/${editing.id}/status`, form);
      toast.success("Case updated — notifications sent");
      setEditing(patched.data);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const filtered = filter === "all" ? checks : checks.filter((c) => c.status === filter);

  return (
    <div>
      <h1 className="font-display text-3xl font-bold text-slate-900">All verification checks</h1>
      <p className="text-slate-600 mt-1 text-sm">Monitor every case across every client, update statuses and set SLA dates.</p>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { k: "Total", v: stats?.total ?? 0, cls: "text-slate-900" },
          { k: "Pending", v: stats?.by_status.Pending ?? 0, cls: "text-amber-700" },
          { k: "In Progress", v: stats?.by_status["In Progress"] ?? 0, cls: "text-blue-700" },
          { k: "Completed", v: stats?.by_status.Completed ?? 0, cls: "text-emerald-700" },
        ].map((s) => (
          <div key={s.k} className="border border-slate-200 rounded-2xl bg-white p-5">
            <div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">{s.k}</div>
            <div className={`mt-2 font-display text-3xl font-bold ${s.cls}`}>{s.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 border border-slate-200 rounded-2xl bg-white overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-semibold text-slate-900">Cases</div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Filter</span>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="h-9 w-40" data-testid="admin-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500 text-sm">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">No cases yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ref #</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Candidate</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Promised by</TableHead>
                <TableHead>Files</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((c) => {
                const overdue = isOverdue(c);
                return (
                  <TableRow key={c.id} data-testid={`admin-check-row-${c.reference_number}`}>
                    <TableCell className="font-mono-data text-xs">{c.reference_number}</TableCell>
                    <TableCell>
                      <div className="text-sm font-medium text-slate-900">{c.client_company || c.client_name}</div>
                      <div className="text-xs text-slate-500">{c.client_email}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {CHECK_TYPE_LABEL[c.check_type]}
                      {c.litigation_type ? <span className="ml-1 text-xs text-slate-500">({c.litigation_type})</span> : null}
                    </TableCell>
                    <TableCell>{c.candidate_name}</TableCell>
                    <TableCell>{c.country}</TableCell>
                    <TableCell><StatusBadge status={c.status} /></TableCell>
                    <TableCell className={`text-xs ${overdue ? "text-rose-600 font-semibold" : "text-slate-600"}`} data-testid={`promised-${c.reference_number}`}>
                      {c.promised_by ? (
                        <span className="inline-flex items-center gap-1">
                          {overdue && <AlertCircle size={12} />} {c.promised_by.slice(0, 10)}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-xs font-mono-data">{c.attachments_count || 0}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => openEdit(c)} data-testid={`edit-status-${c.reference_number}`} className="rounded-full">
                        <Pencil size={14} className="mr-1.5" /> Update
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update case</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="text-sm text-slate-600 grid grid-cols-2 gap-3">
                <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">Ref</div>{editing.reference_number}</div>
                <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">Candidate</div>{editing.candidate_name}</div>
                <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">Type</div>{CHECK_TYPE_LABEL[editing.check_type]}</div>
                <div><div className="font-mono-data text-[10px] uppercase tracking-widest text-slate-500">Client</div>{editing.client_email}</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                    <SelectTrigger className="mt-1.5" data-testid="dialog-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="promised_by">Promised by</Label>
                  <Input id="promised_by" type="date" value={form.promised_by}
                    onChange={(e) => setForm((f) => ({ ...f, promised_by: e.target.value }))}
                    data-testid="dialog-promised-input" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Remarks</Label>
                <Textarea rows={3} value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} data-testid="dialog-remarks-input" className="mt-1.5" />
              </div>
              <CaseAttachments caseId={editing.id} attachments={editing.attachments || []} canUpload={false} />
              <p className="text-xs text-slate-500">Updating status notifies the client and admin by email.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Close</Button>
            <Button onClick={save} disabled={saving} data-testid="dialog-save-status" className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
